import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserInfo, UserSession } from "Backend";
import RequestStatus from "redux/slices/RequestStatus";
import { USER_LOGOUT } from "redux/system.action";
import {
  bootstrap,
  fetchHiddenCellIds,
  fetchSavedZoom,
  getAppHome,
  getSession,
  getUserInfo,
  hideCellId,
  PresetDocumentZoom,
  setSavedZoom,
  showCellId,
} from "./session.action";
import { Client, UserType } from "generated/graphql";

export interface SessionState {
  session?: UserSession;
  status: RequestStatus;
  userInfo?: UserInfo;
  userInfoStatus: RequestStatus;
  client?: Client;
  hiddenCellIds: number[];
  savedZoomScale?: number;
  savedZoomOption?: PresetDocumentZoom;
}

const initialState: SessionState = {
  status: RequestStatus.IDLE,
  userInfoStatus: RequestStatus.IDLE,
  hiddenCellIds: [],
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    init: (state, action: PayloadAction<{ session: UserSession }>) => {
      state.session = action.payload.session;
    },
    clear: (state) => {
      state.session = undefined;
    },
    setUserInfo: (state, action: PayloadAction<UserInfo>) => {
      state.userInfo = action.payload;
      state.userInfoStatus = RequestStatus.SUCCEEDED;
    },
    setProfileFields: (state, action: PayloadAction<{ firstName: string; lastName: string; userType: UserType }>) => {
      const { firstName, lastName, userType } = action.payload;
      if (state.userInfo) {
        state.userInfo.firstName = firstName;
        state.userInfo.lastName = lastName;
        state.userInfo.userType = userType;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSession.pending, (state) => {
        state.status = RequestStatus.PENDING;
      })
      .addCase(getSession.fulfilled, (state, action) => {
        state.session = action.payload;
        state.status = RequestStatus.SUCCEEDED;
      })
      .addCase(getSession.rejected, (state) => {
        state.status = RequestStatus.FAILED;
      });

    builder
      .addCase(getAppHome.pending, (state) => {
        state.status = RequestStatus.PENDING;
      })
      .addCase(getAppHome.fulfilled, (state, action) => {
        state.session = action.payload.users.session;
        state.status = RequestStatus.SUCCEEDED;
      });

    builder.addCase(bootstrap.fulfilled, (state, action) => {
      state.userInfoStatus = RequestStatus.SUCCEEDED;
      state.userInfo = action.payload.users.self;
      state.client = action.payload.client.data;
    });

    builder.addCase(getUserInfo.fulfilled, (state, action) => {
      state.userInfoStatus = RequestStatus.SUCCEEDED;
      state.userInfo = action.payload;
    });

    builder.addCase(fetchHiddenCellIds.fulfilled, (state, action) => {
      state.hiddenCellIds = action.payload;
    });

    builder.addCase(hideCellId.fulfilled, (state, action) => {
      if (action.payload) {
        state.hiddenCellIds = action.payload;
      }
    });

    builder.addCase(showCellId.fulfilled, (state, action) => {
      if (action.payload) {
        state.hiddenCellIds = action.payload;
      }
    });

    builder.addCase(fetchSavedZoom.fulfilled, (state, action) => {
      state.savedZoomScale = action.payload.scale;
      state.savedZoomOption = action.payload.option;
    });
    builder.addCase(setSavedZoom.fulfilled, (state, action) => {
      state.savedZoomScale = action.payload.scale;
      state.savedZoomOption = action.payload.option;
    });

    builder.addCase(USER_LOGOUT, () => {
      return { ...initialState, session: undefined };
    });
  },
});

export default sessionSlice;
export const { init, clear, setUserInfo, setProfileFields } = sessionSlice.actions;
